// Board tab membership and ordering. Adapted from EveryInc/tend src/feed/selectors.ts (MIT,
// Copyright (c) 2026 Every, Inc.): pass filter and tab membership kept; reading groups dropped;
// ordering made stable across refetches (ADHD principle 17: nothing reorders under the cursor).
import type { BoardView, CardSummary, RoutineGroupView } from "@shared/api";
import type { RoundOrder } from "@shared/startEasily";

export type BoardTab = "review" | "queued" | "working" | "done";
export const BOARD_TABS: readonly BoardTab[] = ["review", "queued", "working", "done"];
export const TAB_LABEL: Record<BoardTab, string> = {
  review: "To review",
  queued: "Lined up",
  working: "Claude's on it",
  done: "Done",
};

export function isToReview(card: Pick<CardSummary, "status">): boolean {
  return card.status === "to_review_new" || card.status === "to_review_updated";
}

/**
 * A to-review card belongs to the current pass when it was made ready for it, or when it is an
 * updated member whose newer revision waits for the next pass (it keeps its place, showing the
 * presented revision). A new card made ready for a later pass waits ("Next round ready").
 * The server sends only pass members for review, so this matters for cards that are also listed
 * for other reasons (e.g. "Needs you now").
 */
export function inCurrentPass(
  card: Pick<CardSummary, "readyForPass" | "status">,
  currentPass: number,
): boolean {
  return card.readyForPass <= currentPass || card.status === "to_review_updated";
}

/** Which tab shows a card; "next" means it waits for the next pass; null means not shown. */
export function cardTab(card: CardSummary, currentPass: number): BoardTab | "next" | null {
  if (card.routineGroupId) return null; // shown inside its routine group
  if (card.status === "blocked" || card.work?.status === "blocked") return "working";
  switch (card.status) {
    case "to_review_new":
    case "to_review_updated":
      // U6: a pinned card stays in view across rounds, even if set aside or ready for the next.
      if (card.pinned) return "review";
      if (card.sweepHidden) return null;
      return inCurrentPass(card, currentPass) ? "review" : "next";
    case "queued":
      return "queued";
    case "working":
      return "working";
    case "done":
      // Made time for (U3): it waits under Lined up until its focus block starts.
      return card.disposition === "snoozed" && card.focusBlock ? "queued" : "done";
  }
  return null;
}

export function routineTab(group: Pick<RoutineGroupView, "status">): BoardTab | null {
  switch (group.status) {
    case "proposed":
      return "review";
    case "approved":
      return "queued";
    case "executing":
      return "working";
    case "completed":
    case "partial":
      return "done";
    default:
      return null;
  }
}

/**
 * Server order, with sweep rank (when present) first. Stable: ties keep their server position. A
 * round started quick-first (U3) keeps the server's order exactly: it was chosen as the round
 * started and must not change under the cursor.
 */
export function reviewOrder(
  cards: readonly CardSummary[],
  order: RoundOrder = "standard",
): CardSummary[] {
  if (order === "quick_first") return [...cards];
  return cards
    .map((card, index) => ({ card, index }))
    .sort((a, b) => {
      const ra = a.card.sweepRank ?? Number.MAX_SAFE_INTEGER;
      const rb = b.card.sweepRank ?? Number.MAX_SAFE_INTEGER;
      return ra - rb || a.index - b.index;
    })
    .map((x) => x.card);
}

function byCompletedDesc(a: CardSummary, b: CardSummary): number {
  return (b.completedAt ?? b.updatedAt).localeCompare(a.completedAt ?? a.updatedAt);
}

export interface BoardSections {
  review: CardSummary[];
  queued: CardSummary[];
  working: CardSummary[];
  done: CardSummary[];
  /** Breakthrough cards still to review, in or out of this pass ("Needs you now"). */
  breakthrough: CardSummary[];
  routine: Record<BoardTab, RoutineGroupView[]>;
}

export function boardSections(
  board: Pick<BoardView, "cards" | "routineGroups" | "pass">,
): BoardSections {
  const current = board.pass.current;
  const sections: BoardSections = {
    review: [],
    queued: [],
    working: [],
    done: [],
    breakthrough: [],
    routine: { review: [], queued: [], working: [], done: [] },
  };
  for (const card of board.cards) {
    const tab = cardTab(card, current);
    if (tab && tab !== "next") sections[tab].push(card);
    if (card.breakthrough && isToReview(card) && !card.sweepHidden && !card.routineGroupId) {
      sections.breakthrough.push(card);
    }
  }
  sections.review = reviewOrder(sections.review, board.pass.order);
  sections.done.sort(byCompletedDesc);
  for (const group of board.routineGroups) {
    const tab = routineTab(group);
    if (tab) sections.routine[tab].push(group);
  }
  return sections;
}

/**
 * Keep the order the reader has already seen: known ids keep their relative position, ids that
 * left disappear, new ids are appended at the end (never inserted above the cursor).
 */
export function stableOrder(previous: readonly string[] | null, next: readonly string[]): string[] {
  if (!previous?.length) return [...next];
  const present = new Set(next);
  const kept = previous.filter((id) => present.has(id));
  const seen = new Set(kept);
  return [...kept, ...next.filter((id) => !seen.has(id))];
}

/** Progress through a pass: ids seen in it this session vs. ids still to review. */
export function passProgress(
  seen: readonly string[],
  stillToReview: readonly string[],
): { decided: number; total: number } {
  const open = new Set(stillToReview);
  const total = new Set([...seen, ...stillToReview]).size;
  const decided = [...new Set(seen)].filter((id) => !open.has(id)).length;
  return { decided, total };
}

/** The id to select after the list changed: the same id, else whatever took its place. */
export function reselect(
  ids: readonly string[],
  selected: string | null,
  lastIndex: number,
): string | null {
  if (!ids.length) return null;
  if (selected && ids.includes(selected)) return selected;
  const index = Math.min(Math.max(lastIndex, 0), ids.length - 1);
  return ids[index] ?? null;
}

/** Count shown in the round bar: cards plus proposed routine groups ("N in this round"). */
export function passCount(sections: BoardSections): number {
  return sections.review.length + sections.routine.review.length;
}

/** Past this many rows the Done tab renders in a window (only rows near the viewport). */
export const VIRTUALIZE_AFTER = 50;

export function shouldVirtualize(tab: BoardTab, rows: number): boolean {
  return tab === "done" && rows > VIRTUALIZE_AFTER;
}

const ROUTINE_PREFIX = "routine:";

/** List id for a routine group where groups and cards share one j/k list (board, focus). */
export function routineNavId(groupId: string): string {
  return `${ROUTINE_PREFIX}${groupId}`;
}

export function groupIdOfNavId(id: string | null | undefined): string | null {
  return id?.startsWith(ROUTINE_PREFIX) ? id.slice(ROUTINE_PREFIX.length) : null;
}
